import * as React from 'react'
import './index.scss'
import Box from '@mui/material/Box'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Odds from '../odds'
import Analysis from '../analysis'
import GameStatistics from '../game-statistics'
import GameH2H from '../game-h2h'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useAppDispatch, useAppSelector } from '../../redux'
import SoccerGameTimeline from '../charts/soccer-game-timeline'
import {
    details,
    disableActiveGame,
    fetchAllGameData,
} from '../../redux/slices/fixtures'
import {
    formatGameMinute,
    H2H_TAB_INDEX,
    ODDS_TAB_INDEX,
    EVENTS_TAB_INDEX,
    STATISTICS_TAB_INDEX,
    ANALYSIS_TAB_INDEX,
    FETCH_INTERVAL,
} from '../../utils'
import { updateGameActiveTab } from '../../redux/slices/settings'
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer'
import _ from 'lodash'
import GameEvents from '../game-events'
import moment from 'moment'
import { useEffect } from 'react'
import PullToRefresh from 'react-simple-pull-to-refresh'
import { useParams } from 'react-router-dom'

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    )
}

function a11yProps(index: number) {
    return {
        id: `tab-${index}`,
        'aria-controls': `tabpanel-${index}`,
    }
}

export default function GameDetails(props: any) {
    const dispatch = useAppDispatch()

    const { matchId } = useParams()

    const { activeGame, odds, headToHeadFixtureId } = useAppSelector((state) => state.fixtures)
    const { gameActiveTab } = useAppSelector((state) => state.settings)

    useEffect(() => {
        let timer: any = null

        if (matchId) {
            const fetchDataOnInterval = async () => {
                try {
                    dispatch(details(matchId))

                    if (activeGame.status.type !== 'inprogress' && timer) {
                        clearInterval(timer)
                    }
                } catch (error) {
                    clearInterval(timer)
                }
            }

            dispatch(updateGameActiveTab(ODDS_TAB_INDEX))
            dispatch(fetchAllGameData(parseInt(matchId, 10)))

            fetchDataOnInterval()
            timer = setInterval(fetchDataOnInterval, FETCH_INTERVAL)

            // Function to handle visibility change
            const handleVisibilityChange = () => {
                if (!document.hidden) {
                    // The page has become visible, refresh your content
                    dispatch(details(matchId))
                }
            }

            // Add event listener for visibility change
            document.addEventListener('visibilitychange', handleVisibilityChange)

            return () => {
                if (timer) {
                    clearInterval(timer)
                }
                // Clean up the event listener on component unmount
                document.removeEventListener('visibilitychange', handleVisibilityChange)
            }
        }
    }, [matchId])

    if (!matchId || !activeGame) {
        return (
            <div className="no-available-games">
                <SportsSoccerIcon />
                <h4>No game selected.</h4>
                <p>Please select a game in order to see more details.</p>
            </div>
        )
    }

    const handleTabChange = (event: React.SyntheticEvent, newTab: number) => {
        if (newTab === H2H_TAB_INDEX && parseInt(headToHeadFixtureId, 10) !== activeGame.id) {
            dispatch(fetchAllGameData(activeGame.id))
        }

        dispatch(updateGameActiveTab(newTab))
    }

    const goBack = () => {
        dispatch(updateGameActiveTab(gameActiveTab))
        dispatch(disableActiveGame())
    }

    const momentGame = moment(activeGame.startTimestamp * 1000)

    const handleRefresh = async () => {
        await dispatch(details(activeGame.id))
    }

    function renderGameStatus(game: any) {
        if (['Coverage canceled', 'Ended'].includes(game.status.description)) {
            return 'Ended'
        }

        return game.status.description
    }

    return (
        <PullToRefresh onRefresh={handleRefresh} pullingContent={<span></span>}>
            <div className="game-details">
                {activeGame.id && (
                    <div className="back-button">
                        <ArrowBackIcon onClick={() => goBack()} />
                    </div>
                )}
                <div className="competition-name">
                    <div
                        className="logo"
                        style={{
                            backgroundImage: `url(${activeGame.tournament?.uniqueTournament?.image})`,
                        }}
                    />
                    {activeGame.tournament.name}
                    {activeGame.roundInfo && ', Round ' + activeGame.roundInfo.round}
                </div>
                <div className="board">
                    <div className="home">
                        <div
                            className="logo"
                            style={{
                                backgroundImage: `url(${
                                    activeGame.homeTeam?.image ||
                                    `https://api.sofascore.app/api/v1/team/${activeGame.homeTeam.id}/image`
                                })`,
                            }}
                        ></div>
                        <div className="name">{activeGame.homeTeam.shortName}</div>
                    </div>
                    <div className="time">
                        <div
                            className={
                                'score' + (activeGame.status.type === 'inprogress' ? ' live-game' : '')
                            }
                        >
                            {activeGame.status.type === 'notstarted' && (
                                <div className="minute" style={{ fontWeight: 700, fontSize: '18px' }}>
                                    {momentGame.format('HH:mm')}
                                </div>
                            )}

                            {activeGame.homeScore.display !== activeGame.homeScore.current ? (
                                <span>
                                    {activeGame.homeScore.display}
                                    <span style={{ fontSize: '13px' }}>{activeGame.homeScore.current}</span>
                                </span>
                            ) : (
                                activeGame.homeScore.hasOwnProperty('display') && (
                                    <span>{activeGame.homeScore.display}</span>
                                )
                            )}
                            {activeGame.status.type !== 'notstarted' && <span>-</span>}
                            {activeGame.awayScore.display !== activeGame.awayScore.current ? (
                                <span>
                                    <span style={{ fontSize: '13px' }}>{activeGame.awayScore.current}</span>
                                    {activeGame.awayScore.display}
                                </span>
                            ) : (
                                activeGame.awayScore.hasOwnProperty('display') && (
                                    <span>{activeGame.awayScore.display}</span>
                                )
                            )}
                        </div>
                        {activeGame.status.type === 'inprogress' ? (
                            <div className="minute live-game">
                                {activeGame.status.description === 'Halftime' ? (
                                    'HT'
                                ) : (
                                    <>
                                        {formatGameMinute(activeGame)}
                                        <span style={{ animation: 'blink 1s infinite' }}>'</span>
                                    </>
                                )}
                            </div>
                        ) : activeGame.status.type === 'notstarted' ? (
                            <div className="minute" style={{ color: '#81858b' }}>
                                {momentGame.calendar().slice(0, momentGame.calendar().indexOf('at'))}
                            </div>
                        ) : (
                            <div className="minute">{renderGameStatus(activeGame)}</div>
                        )}

                        {!momentGame.isSame(new Date(), 'day') && (
                            <div className="date">{momentGame.format('DD.MM.YYYY')}</div>
                        )}
                    </div>
                    <div className="away">
                        <div
                            className="logo"
                            style={{
                                backgroundImage: `url(${
                                    activeGame.awayTeam?.image ||
                                    `https://api.sofascore.app/api/v1/team/${activeGame.awayTeam.id}/image`
                                })`,
                            }}
                        ></div>
                        <div className="name">{activeGame.awayTeam.shortName}</div>
                    </div>
                </div>
                <div className="details card-shadow">
                    <div className="scoreboard-summary-wrapper">
                        <div className="scoreboard-summary">
                            <div className="red-cards">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="presentation"
                                    focusable="false"
                                    className="kz-icon-xxs icon--color-afrodite scoreboard-summary__item__icon srt-fill-soccer-red-card"
                                    style={{width: '16px', height: '16px'}}
                                >
                                    <rect width="14" height="18" x="5" y="3" rx="2"></rect>
                                </svg>
                                <div className='value'>
                                    {activeGame.homeRedCards || 0}
                                    -
                                    {activeGame.awayRedCards || 0}
                                </div>
                            </div>
                            <div className="yellow-cards">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="presentation"
                                     focusable="false"
                                     className="kz-icon-xxs icon--color-afrodite scoreboard-summary__item__icon yellow-card">
                                    <rect width="14" height="18" x="5" y="3" rx="2"></rect>
                                </svg>
                                <div className='value'>
                                    {activeGame.incidents?.filter((el) => el.incidentType === 'card' && el.incidentClass === 'yellow' && el.isHome === '1').length}
                                    -
                                    {activeGame.incidents?.filter((el) => el.incidentType === 'card' && el.incidentClass === 'yellow' && el.isHome === '0').length}
                                </div>
                            </div>
                            <div className="corners">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="presentation"
                                     focusable="false" className="kz-icon-xxs icon--color-white-snow">
                                    <path
                                        d="M18 7.136L10.615 4v14.4H8.77v-1.384C7.117 17.296 6 17.808 6 18.4c0 .88 2.483 1.6 5.538 1.6 3.056 0 5.539-.72 5.539-1.6 0-.792-1.994-1.448-4.615-1.576v-7.24L18 7.136z"></path>
                                </svg>
                                <div className='value'>
                                    {activeGame.statistics?.find((el) => el.eventPeriod === 'ALL' && el.name === 'Corner kicks')?.homeValue}
                                    -
                                    {activeGame.statistics?.find((el) => el.eventPeriod === 'ALL' && el.name === 'Corner kicks')?.awayValue}
                                </div>
                            </div>
                            <div className="shots">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
                                     viewBox="0 0 24 24"
                                     role="presentation" focusable="false" className="kz-icon-s">
                                    <ellipse cx="18.184" cy="6.982" fill="#EBEBEB" rx="5" ry="4.982"></ellipse>
                                    <path
                                        d="M22.156 4.472a.039.039 0 00-.024-.01l.04-.536a5.052 5.052 0 00-.824-.844h-.006a4.085 4.085 0 00-.316-.239 11.523 11.523 0 00-.16-.107l-.01-.006a6.512 6.512 0 00-.334-.191l-.01-.006a4.528 4.528 0 00-.171-.085l-.012-.005a4.638 4.638 0 00-.36-.15l-.018-.007a7.21 7.21 0 00-.18-.063h-.01a2.83 2.83 0 00-.183-.06c-.094.464-.184.811-.215.926-1.333.179-2.065.596-2.188.667l-.007.004c-.138-.086-.672-.381-1.57-.472.012-.225.046-.448.1-.667l-.1.056a3.79 3.79 0 00-.08.63c-.165.093-.807.497-1.422 1.53h-.01a2.93 2.93 0 00-.518.145l-.044.1c.18-.075.366-.13.558-.166.03.144.178.696.733 1.514l-.007.024c-.048.166-.338 1.166-.27 2.185 0 .006.003.013.007.018a3.952 3.952 0 01-.898.383l-.025.065a5.013 5.013 0 00.866 1.277c.086.092.175.181.268.267l.006.006c.14.13.287.25.441.363l.01.006c.102.081.207.155.314.222l.01.005c.053.034.108.067.162.1l.1-.04s.315-.025.505-.054l.008.007a8.364 8.364 0 001.696.736H18.326c-.977-.218-1.96-.795-1.97-.804a4.195 4.195 0 01-.043-.876l.065-.018c.344-.097 1.333-.374 2.07-.714.165.135.97.748 1.965.841a3.734 3.734 0 01-.2 1.143l.1-.044c.115-.364.175-.744.178-1.126.186-.156 1.013-.883 1.382-1.776h.028c.39-.016.773-.11 1.126-.273l.007-.025.02-.075c-.626.278-1.06.298-1.17.292-.03-.174-.2-.92-.89-1.77.279-1.331.259-2.063.252-2.187.263-.013.711-.066.844-.083.001.01.005.018.012.024a4.38 4.38 0 011.079 1.845 5.065 5.065 0 00-.08-.462 4.416 4.416 0 00-.945-1.44zm-3.732 5.04c-.813.375-1.948.679-2.157.733-.214-.149-1.126-.81-1.668-1.576a.039.039 0 00.017-.034c-.073-1.094.27-2.168.274-2.18a.04.04 0 000-.006c.59-.265 1.197-.493 1.816-.682l.004.003c.064.053.75.62 1.975 1.693-.025.244-.147 1.39-.26 2.05zm.312-2.108c.74-.172 1.47-.39 2.183-.65.285-1.363.252-2.084.248-2.169v-.008c0-.009.002-.018.007-.025a5.999 5.999 0 00-1.76-1.392c-1.35.175-2.09.602-2.196.663a.142.142 0 01-.026.012c-.183.615-.328 1.24-.433 1.873l.013.011c.113.094.793.658 1.964 1.685z"
                                    ></path>
                                    <path
                                        d="M5.154 9.162c-.038-.317-.069-.569-.24-.615-.135-.036-.366.06-.762.355-1.4 1.08-1.911 3.182-2.287 4.728-.04.167-.08.328-.118.48-.267 1.058.163 1.298 1.056 1.537.116.031.24.063.372.096.149.038.307.078.474.123.22.059.456.126.703.209 1.16.385 9.503 2.902 11.18 3.351 1.676.45 4.037.1 4.404-1.271.238-.888-.685-1.449-1.726-2.08-.568-.345-1.17-.71-1.638-1.161-1.333-1.285-3.812-2.52-3.812-2.52-.025-.583-.634-.8-.862-.861a.748.748 0 00-.084-.019c-.013-.687-1.07-.97-1.07-.97L8.535 7.708a.287.287 0 00-.15-.102.295.295 0 00-.26.058l-1.131.919c-.452.37.218 1.09.732 1.643.33.356.597.642.46.722-.224.13-.752.192-1.374.025a3.436 3.436 0 01-1.101-.527c-.442-.315-.507-.858-.558-1.284zm4.982 7.278l-1.388-.372c-.093-.025-.106-.157-.022-.2l4.192-2.232c.028-.017.068-.014.1.002l.972.572c.07.039.07.146-.006.188l-3.773 2.033a.11.11 0 01-.075.01zm-2.72-.754l-1.469-.372c-.093-.025-.106-.157-.021-.205l5.174-2.685a.107.107 0 01.1.002l.969.57c.072.045.068.151-.008.193l-4.67 2.488a.11.11 0 01-.076.01zm-4.342.477c.151.036.301.077.466.12.244.066.457.127.664.195l-.538 1.152a.106.106 0 01-.124.058l-1.039-.278a.109.109 0 01-.078-.112l.083-1.287.188.05c.118.037.242.07.378.102zm3.359 2.23a.105.105 0 01-.124.057l-1.04-.278a.108.108 0 01-.078-.112l.085-1.245c.456.143 1.025.316 1.653.505l-.496 1.072zm7.892 2.384a.105.105 0 00.124-.058l.474-1.016a345.03 345.03 0 01-1.638-.476l-.077 1.16a.108.108 0 00.078.111l1.04.279zm3.086-.669l-.584 1.248a.105.105 0 01-.124.058l-1.039-.279a.109.109 0 01-.078-.112l.073-1.127c.562.121 1.17.164 1.751.125a.117.117 0 010 .087z"
                                    ></path>
                                </svg>
                                <div className='value'>
                                    {activeGame.statistics?.find((el) => el.eventPeriod === 'ALL' && el.name === 'Shots on target')?.homeValue}
                                    -
                                    {activeGame.statistics?.find((el) => el.eventPeriod === 'ALL' && el.name === 'Shots on target')?.awayValue}
                                </div>
                            </div>
                            <div className="goals">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                     role="presentation" focusable="false" className="kz-icon-xxs">
                                    <g transform="translate(3 3)" fill="none">
                                        <circle cx="8.93" cy="8.93" r="8.93" fill="#EBEBEB"></circle>
                                        <path fill="#414952"
                                              d="M16.025 4.432a.07.07 0 00-.042-.018c.037-.46.073-.962.073-.962a9.045 9.045 0 00-.695-.8 9.045 9.045 0 00-.78-.713h-.01a7.322 7.322 0 00-.563-.428 20.536 20.536 0 00-.285-.192l-.018-.01a11.64 11.64 0 00-.597-.343l-.02-.01a8.08 8.08 0 00-.304-.152l-.022-.01a8.253 8.253 0 00-.643-.268l-.032-.013C11.98.474 11.874.436 11.766.4h-.018A5.04 5.04 0 0011.42.29a27.88 27.88 0 01-.383 1.661c-2.43.328-3.743 1.102-3.92 1.203-.247-.153-1.2-.682-2.804-.844.02-.404.08-.804.178-1.196l-.178.1c-.08.372-.128.75-.144 1.13-.295.165-1.44.89-2.54 2.742H1.61a5.216 5.216 0 00-.925.26l-.078.179c.32-.135.654-.235.996-.298.055.259.319 1.248 1.31 2.714 0 .02-.627 1.97-.496 3.96a.068.068 0 00.013.032 7.045 7.045 0 01-1.604.687l-.045.117a8.99 8.99 0 00.891 1.492 9.045 9.045 0 001.136 1.276l.01.01c.25.232.514.45.788.651l.016.01c.184.147.371.28.562.398l.017.011.29.178.18-.071s.562-.045.9-.096l.015.012c.958.55 1.974.993 3.03 1.319H9.185c-1.746-.39-3.5-1.426-3.518-1.442a7.55 7.55 0 01-.078-1.57c.47-.134 2.4-.658 3.812-1.312.296.241 1.734 1.34 3.51 1.508a6.715 6.715 0 01-.356 2.048l.179-.079a6.902 6.902 0 00.317-2.017c.333-.28 1.81-1.584 2.468-3.185h.052a5.286 5.286 0 002.01-.49l.048-.178c-1.117.497-1.892.535-2.09.524-.054-.312-.357-1.649-1.588-3.174.497-2.387.461-3.698.449-3.921.47-.023 1.27-.118 1.508-.148.001.016.009.032.021.043a7.858 7.858 0 011.927 3.308 9.067 9.067 0 00-.141-.83 7.916 7.916 0 00-1.69-2.58zM9.36 13.468c-1.452.67-3.479 1.215-3.853 1.313-.381-.267-2.01-1.454-2.98-2.826a.07.07 0 00.03-.06c-.13-1.961.483-3.888.49-3.907v-.013a27.907 27.907 0 013.244-1.222c.043.035 1.274 1.055 3.534 3.04-.044.438-.262 2.492-.465 3.675zm4.455-4.946a33.473 33.473 0 01-3.897 1.166c-2.205-1.94-3.434-2.958-3.532-3.04.188-1.134.447-2.256.773-3.358a.07.07 0 00.023-.009c.013 0 1.344-.863 3.948-1.201a10.72 10.72 0 013.142 2.495.07.07 0 00-.013.044c-.002.015.096 1.312-.444 3.903z"></path>
                                    </g>
                                </svg>

                                <div className="home-value">
                                    {activeGame.incidents
                                        ?.filter((el) => el.incidentType === 'goal' && el.isHome === '1')
                                        .map((i) => <span key={i.id + '-time-' + i.time}>{i.time}, </span>)
                                    }
                                </div>

                                <div className="away-value">
                                    {activeGame.incidents
                                        ?.filter((el) => el.incidentType === 'goal' && el.isHome === '0')
                                        .map((i) => <span key={i.id + '-time-' + i.time}>{i.time}, </span>)
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    <SoccerGameTimeline gameId={activeGame.id}/>
                    <Box sx={{borderBottom: 1, borderColor: 'var(--border-box-color)'}}>
                        <Tabs value={gameActiveTab} onChange={handleTabChange} aria-label="basic"
                              className='game-details-tabs'>
                            <Tab label="Odds" {...a11yProps(ODDS_TAB_INDEX)} />
                        <Tab label="Statistics" {...a11yProps(STATISTICS_TAB_INDEX)} disabled={!activeGame.statistics} />
                            <Tab label="H2H" {...a11yProps(H2H_TAB_INDEX)} />
                        <Tab label="Events" {...a11yProps(EVENTS_TAB_INDEX)} disabled={!activeGame.incidents} />
                            <Tab label="Bet Assist" {...a11yProps(ANALYSIS_TAB_INDEX)} />
                        </Tabs>
                    </Box>
                    <TabPanel value={gameActiveTab} index={ANALYSIS_TAB_INDEX}>
                        <Analysis game={activeGame}/>
                    </TabPanel>
                    <TabPanel value={gameActiveTab} index={ODDS_TAB_INDEX}>
                        <Odds data={odds}/>
                    </TabPanel>
                    {
                        activeGame.statistics &&
                        <TabPanel value={gameActiveTab} index={STATISTICS_TAB_INDEX}>
                            <GameStatistics data={activeGame.statistics}/>
                        </TabPanel>
                    }
                    <TabPanel value={gameActiveTab} index={H2H_TAB_INDEX}>
                        <GameH2H/>
                    </TabPanel>
                    {
                        activeGame.incidents &&
                        <TabPanel value={gameActiveTab} index={EVENTS_TAB_INDEX}>
                            <GameEvents data={
                                activeGame.incidents?.filter((i) => i.incidentType === 'penaltyShootout')
                                    .concat(_.cloneDeep(activeGame.incidents)
                                        ?.filter((i) => i.incidentType !== 'penaltyShootout')
                                        .sort((i1, i2) => i1.time === '-' && i2.time === '-' ?
                                            (i2.externalId - i1.externalId) :
                                            (
                                                (parseInt(i2.time, 10) + parseInt(i2.incidentType === 'period' ? 0 : (i2.addedTime || 0)))
                                                -
                                                (parseInt(i1.time, 10) + parseInt(i1.incidentType === 'period' ? 0 : (i1.addedTime || 0)))
                                            )
                                        ))
                            }
                            />
                        </TabPanel>
                    }

                </div>
            </div>
        </PullToRefresh>
    )
}
